// ════════════════════════════════════════════════════════════════════════
// TurnstileWidget.tsx — Widget de Cloudflare Turnstile reutilizable (inline)
// ════════════════════════════════════════════════════════════════════════
// TurnstileGate.tsx bloquea el sitio ENTERO una sola vez por carga de
// página. Este componente extrae la misma lógica de render/callback para
// usarla en puntos puntuales — el formulario de contacto y el pop-up de
// login — donde cada acción se verifica por separado, dentro del propio
// formulario, en vez de una pantalla completa aparte.
//
// El script de Cloudflare (index.html, render=explicit) ya está cargado
// globalmente — acá solo se hace polling de window.turnstile, se
// renderiza el widget y se valida el token contra POST /turnstile/verify
// antes de avisarle al padre (onVerified) que ya puede habilitar su acción
// (el botón de enviar el form, o el de iniciar sesión).
// ════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@/shared/context/ThemeContext';
import styles from './TurnstileWidget.module.css';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          theme?: 'light' | 'dark' | 'auto';
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
        }
      ) => string;

      remove: (widgetId: string) => void;
    };
  }
}

interface TurnstileWidgetProps {
  // Se llama cuando Cloudflare Y el backend (POST /turnstile/verify)
  // confirman que el desafío es válido.
  onVerified: () => void;
  // Se llama ante cualquier falla (widget, red, o backend) — el padre
  // debería volver a bloquear su acción hasta un nuevo onVerified.
  onError?: () => void;
}

export function TurnstileWidget({ onVerified, onError }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);
  // Sigue el mismo tema que el resto del sitio: al montar arranca en lo
  // que ThemeContext ya haya resuelto (dispositivo, si es la primera
  // visita), y si el usuario lo cambia manualmente con el toggle, este
  // efecto se vuelve a ejecutar (theme está en las dependencias más abajo)
  // — Cloudflare no soporta cambiar el theme de un widget ya renderizado,
  // así que el cleanup lo destruye y se vuelve a renderizar con el nuevo
  // color. Esto obliga a resolver el desafío de nuevo tras el cambio.
  const { theme } = useTheme();

  useEffect(() => {
    let widgetId: string | undefined;
    let cancelled = false;

    const renderTurnstile = () => {
      if (cancelled || !containerRef.current || !window.turnstile) {
        return;
      }

      widgetId = window.turnstile.render(containerRef.current, {
        sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
        theme,

        callback: async (token: string) => {
          try {
            const leadsApiUrl = import.meta.env.VITE_LEADS_API_URL ?? 'http://localhost:5199';

            const response = await fetch(`${leadsApiUrl}/turnstile/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token }),
            });

            if (!response.ok) {
              throw new Error('Turnstile verification failed');
            }

            const result = await response.json();

            if (result.success) {
              setError(false);
              onVerified();
            } else {
              setError(true);
              onError?.();
            }
          } catch (err) {
            console.error(err);
            setError(true);
            onError?.();
          }
        },

        'error-callback': () => {
          setError(true);
          onError?.();
        },

        // El token expiró (unos minutos) — Cloudflare vuelve a mostrar el
        // widget solo; hasta que el usuario lo resuelva de nuevo, avisamos
        // al padre para que vuelva a bloquear su botón.
        'expired-callback': () => {
          onError?.();
        },
      });
    };

    // El script de Cloudflare puede todavía estar cargando.
    const interval = window.setInterval(() => {
      if (window.turnstile) {
        window.clearInterval(interval);
        renderTurnstile();
      }
    }, 100);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [onVerified, onError, theme]);

  return (
    <div className={styles.wrapper}>
      <div ref={containerRef} className={styles.turnstile} />
      {error && (
        <p className={styles.error} role="alert">
          No pudimos verificar que sos humano. Intenta de nuevo.
        </p>
      )}
    </div>
  );
}
